import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import {
  UserPlus, Users, Wallet, Loader2, Ban, FileText, CalendarClock,
} from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import Modal from '../ui/Modal';
import { Buscador } from '../ui/Filtros';

// A QUIÉN LE VENDES, y cuánto te debe.
//
// TRES DECISIONES DE ESTA PANTALLA, y las tres son de usuario cero:
//
//   1. SOLO EL NOMBRE ES OBLIGATORIO. Lo fiscal —RFC, régimen, código
//      postal— va plegado y solo hace falta el día que haya que
//      facturarle. Un alta que pide la constancia de situación fiscal
//      para apuntar a quién se le fía es un alta que nadie hace, y quien
//      no la hace acaba llevando la lista en una libreta.
//
//   2. EL CRÉDITO VA EN PAREJA Y SE DICE POR QUÉ. «Te fío $5 000» sin
//      plazo no significa nada, y «a 30 días» sin importe tampoco. El
//      botón se queda apagado CON LA EXPLICACIÓN A LA VISTA en vez de
//      dejar que se descubra al guardar.
//
//   3. NO SE DA DE BAJA A QUIEN DEBE. La deuda desaparecería de la lista
//      sin haberse cobrado, y nadie volvería a acordarse. Se dice con la
//      cifra delante, no con un «no se puede».

const LIMPIO = {
  nombre: '', telefono: '', email: '', codigo: '',
  rfc: '', razon_social: '', regimen_fiscal: '', codigo_postal: '', uso_cfdi: '',
  limite_credito: '', dias_credito: '', notas: '',
};

const dinero = (v) => `$${Number(v || 0).toLocaleString('es-MX', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

export default function Clientes() {
  const [pestana, setPestana] = useState('lista');
  useSubpantalla('pestana', pestana !== 'lista', () => setPestana('lista'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });
  const { puede } = useAuth();
  const avisos = useAvisos();
  const [filas, setFilas] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [conDeuda, setConDeuda] = useState(false);
  const [nuevo, setNuevo] = useState(null);      // null = diálogo cerrado
  const [abonando, setAbonando] = useState(null);
  const [viendo, setViendo] = useState(null);

  const puedeCredito = puede('credito');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const q = new URLSearchParams();
      if (buscar.trim()) q.set('busqueda', buscar.trim());
      if (conDeuda) q.set('con_deuda', 'true');
      setFilas(await api.get(`/clientes?${q}`));
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setCargando(false);
    }
  }, [buscar, conDeuda, avisos]);

  useEffect(() => { cargar(); }, [cargar]);

  const columnas = [
    { etiqueta: 'Cliente', valor: (c) => c.nombre, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Teléfono', valor: (c) => c.telefono || '—', clase: 'text-gray-500' },
    { etiqueta: 'RFC', valor: (c) => c.rfc || '—', clase: 'font-mono text-xs text-gray-500' },
    {
      etiqueta: 'Te debe', tipo: 'moneda', valor: (c) => c.saldo,
      render: (c) => (
        <span className={Number(c.vencido) > 0 ? 'font-semibold text-red-600'
          : Number(c.saldo) > 0 ? 'font-semibold text-amber-600' : 'text-gray-400'}>
          {dinero(c.saldo)}
          {Number(c.vencido) > 0 && (
            <span className="block text-xs font-normal">
              {dinero(c.vencido)} ya vencido
            </span>
          )}
        </span>
      ),
    },
    {
      etiqueta: 'Le puedes fiar', tipo: 'moneda', valor: (c) => c.credito_disponible,
      render: (c) => (Number(c.limite_credito) > 0
        ? <span className="text-gray-700">{dinero(c.credito_disponible)}</span>
        : <span className="text-gray-400">Solo de contado</span>),
    },
  ];

  if (pestana === 'antiguedad') {
    return <Antiguedad onVolver={() => setPestana('lista')} />;
  }

  return (
    <>
      {puedeCredito && (
        <div className="px-4 pt-3">
          <button type="button" onClick={() => setPestana('antiguedad')}
                  className="inline-flex items-center gap-2 text-sm font-semibold
                             text-[var(--color-marca)]">
            <CalendarClock className="h-4 w-4" aria-hidden="true" />
            Ver cuánto te deben y desde cuándo
          </button>
        </div>
      )}
      <ListaEstandar
        titulo="Clientes"
        subtitulo="A quién le vendes, cuánto te debe y a quién puedes fiarle."
        nota={
          <NotaExplicativa titulo="Para dar de alta a alguien basta su nombre">
            El crédito viene en cero: mientras no le pongas un límite, ese cliente
            solo puede comprar de contado.
          </NotaExplicativa>
        }
        accionPrincipal={
          <button
            type="button"
            onClick={() => setNuevo(LIMPIO)}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)]
                       px-4 py-2.5 font-semibold text-white"
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            Nuevo cliente
          </button>
        }
        filtros={
          <>
            <Buscador valor={buscar} onChange={setBuscar}
                      placeholder="Nombre o RFC…" />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={conDeuda}
                     onChange={(e) => setConDeuda(e.target.checked)} />
              Solo los que me deben
            </label>
          </>
        }
        columnas={columnas}
        filas={filas || []}
        cargando={cargando}
        claveFila={(c) => c.id}
        acciones={(c) => (c.es_publico_general ? null : (
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setViendo(c)}
                    className="inline-flex items-center gap-1 text-sm font-semibold
                               text-[var(--color-marca)]">
              <FileText className="h-4 w-4" aria-hidden="true" />
              Estado de cuenta
            </button>
            {puedeCredito && Number(c.saldo) > 0 && (
              <button type="button" onClick={() => setAbonando(c)}
                      className="inline-flex items-center gap-1 text-sm font-semibold
                                 text-[var(--color-marca)]">
                <Wallet className="h-4 w-4" aria-hidden="true" />
                Registrar abono
              </button>
            )}
          </div>
        ))}
        exportar={{
          nombre: 'clientes',
          columnas: [
            { etiqueta: 'Cliente', valor: (c) => c.nombre },
            { etiqueta: 'RFC', valor: (c) => c.rfc || '' },
            { etiqueta: 'Teléfono', valor: (c) => c.telefono || '' },
            { etiqueta: 'Correo', valor: (c) => c.email || '' },
            { etiqueta: 'Te debe', tipo: 'moneda', valor: (c) => c.saldo },
            { etiqueta: 'Vencido', tipo: 'moneda', valor: (c) => c.vencido },
            { etiqueta: 'Límite de crédito', tipo: 'moneda', valor: (c) => c.limite_credito },
            { etiqueta: 'Días de crédito', tipo: 'numero', valor: (c) => c.dias_credito },
          ],
        }}
        vacio={
          <EstadoVacio
            icono={Users}
            titulo={buscar ? 'Ningún cliente con ese nombre' : 'Todavía no tienes clientes'}
            descripcion={buscar
              ? 'Prueba con menos letras, o con el RFC. Si es alguien nuevo, dalo de alta.'
              : 'Aquí van las personas y empresas a las que les vendes. Sirve para facturarles, para fiarles y para ver qué te han comprado. Las ventas de mostrador sin nombre siguen saliendo igual que hoy: se registran a «Público en general».'}
            accion={
              <button type="button" onClick={() => setNuevo(LIMPIO)}
                      className="mt-2 inline-flex items-center gap-2 rounded-xl
                                 bg-[var(--color-marca)] px-4 py-2.5 font-semibold text-white">
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Dar de alta al primero
              </button>
            }
          />
        }
      />

      {nuevo && (
        <DialogoCliente
          valores={nuevo}
          puedeCredito={puedeCredito}
          onCerrar={() => setNuevo(null)}
          onGuardado={(c) => {
            setNuevo(null);
            avisos.exito(`${c.nombre} ya está en tu lista de clientes.`);
            cargar();
          }}
        />
      )}

      {viendo && (
        <EstadoCuenta cliente={viendo} onCerrar={() => setViendo(null)} />
      )}

      {abonando && (
        <DialogoAbono
          cliente={abonando}
          onCerrar={() => setAbonando(null)}
          onGuardado={(msg) => { setAbonando(null); avisos.exito(msg); cargar(); }}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------- alta
function DialogoCliente({ valores, puedeCredito, onCerrar, onGuardado }) {
  const [v, setV] = useState(valores);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [verFiscal, setVerFiscal] = useState(false);

  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  // EL BOTÓN SE APAGA CON LA RAZÓN A LA VISTA, nunca en silencio.
  const limite = Number(v.limite_credito || 0);
  const dias = Number(v.dias_credito || 0);
  const motivoApagado =
    !v.nombre.trim() ? 'Escribe al menos el nombre del cliente.'
      : (limite > 0) !== (dias > 0)
        ? 'El crédito lleva las dos cosas: cuánto le fías y a cuántos días te lo paga. Pon las dos, o deja las dos vacías para que sea solo de contado.'
        : v.rfc && ![12, 13].includes(v.rfc.trim().length)
          ? 'El RFC tiene 12 caracteres si es empresa y 13 si es persona.'
          : '';

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      const cuerpo = { nombre: v.nombre.trim() };
      for (const k of ['telefono', 'email', 'codigo', 'rfc', 'razon_social',
                       'regimen_fiscal', 'codigo_postal', 'uso_cfdi', 'notas']) {
        if (v[k] && v[k].trim()) cuerpo[k] = v[k].trim();
      }
      if (limite > 0) {
        cuerpo.limite_credito = String(limite);
        cuerpo.dias_credito = dias;
      }
      onGuardado(await api.post('/clientes', cuerpo));
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal titulo="Nuevo cliente" icono={UserPlus} ancho="max-w-lg"
           subtitulo="Solo el nombre es obligatorio. Lo demás se puede llenar después."
           onCerrar={onCerrar} bloqueado={guardando}
           pie={
             <div className="flex flex-col gap-2">
               {motivoApagado && (
                 <p id="motivo-guardar-cliente" role="note" className="text-sm text-amber-700">{motivoApagado}</p>
               )}
               {error && <p className="text-sm text-red-600">{error}</p>}
               <div className="flex justify-end gap-2">
                 <button type="button" onClick={onCerrar} disabled={guardando}
                         className="px-4 py-2.5 font-semibold text-gray-600">
                   Cancelar
                 </button>
                 <button type="button" onClick={guardar}
                         disabled={!!motivoApagado || guardando}
                         aria-describedby={motivoApagado ? 'motivo-guardar-cliente' : undefined}
                         className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5
                                    font-semibold text-white bg-[var(--color-marca)]
                                    disabled:bg-gray-300">
                   {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                   Guardar cliente
                 </button>
               </div>
             </div>
           }>
      <div className="flex flex-col gap-3">
        <Campo etiqueta="Nombre" obligatorio valor={v.nombre} onChange={set('nombre')}
               ayuda="Como lo conoces tú. Puede ser una persona o un negocio." />
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Teléfono" valor={v.telefono} onChange={set('telefono')} />
          <Campo etiqueta="Correo" valor={v.email} onChange={set('email')} />
        </div>

        {/* CRÉDITO: solo para quien puede decidirlo. */}
        {puedeCredito ? (
          <fieldset className="rounded-xl border border-gray-200 p-3">
            <legend className="px-1 text-sm font-semibold text-gray-700">
              ¿Le vas a fiar?
            </legend>
            <p className="mb-2 text-sm text-gray-500">
              Déjalo vacío si te paga siempre en el momento. Es lo normal, y es
              lo que trae por omisión.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="Le fías hasta" valor={v.limite_credito}
                     onChange={set('limite_credito')} tipo="number"
                     ayuda="En pesos. Cuando llegue a ese total, TEGO no dejará fiarle más." />
              <Campo etiqueta="Te paga a los" valor={v.dias_credito}
                     onChange={set('dias_credito')} tipo="number"
                     ayuda="Días desde la venta. A los 30 es lo más común." />
            </div>
          </fieldset>
        ) : (
          <p className="text-sm text-gray-500">
            El crédito lo fija quien lleva la gerencia. Guarda al cliente y
            pídeselo si le vas a fiar.
          </p>
        )}

        {/* FISCAL: plegado, porque casi nunca hace falta al dar de alta. */}
        <button type="button" onClick={() => setVerFiscal(!verFiscal)}
                className="self-start text-sm font-semibold text-[var(--color-marca)]">
          {verFiscal ? 'Ocultar' : 'Añadir'} los datos para facturarle
        </button>
        {verFiscal && (
          <div className="flex flex-col gap-3 rounded-xl bg-gray-50 p-3">
            <Ayuda id="clientes.datos_fiscales" />
            <p className="text-sm text-gray-600">
              Estos datos tienen que ser <strong>exactamente</strong> los que el cliente
              tiene registrados ante el SAT, o su factura será rechazada. Vienen en su
              constancia de situación fiscal.
            </p>
            <Campo etiqueta="RFC" valor={v.rfc} onChange={set('rfc')}
                   ayuda="12 caracteres si es empresa, 13 si es persona." />
            <Campo etiqueta="Razón social" valor={v.razon_social}
                   onChange={set('razon_social')}
                   ayuda="Solo si su nombre fiscal es distinto del comercial." />
            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="Código postal" valor={v.codigo_postal}
                     onChange={set('codigo_postal')}
                     ayuda="El de su domicilio fiscal. Cinco dígitos." />
              <Campo etiqueta="Régimen fiscal" valor={v.regimen_fiscal}
                     onChange={set('regimen_fiscal')}
                     ayuda="El número de tres cifras de su constancia. Ej.: 601." />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------- abono
function DialogoAbono({ cliente, onCerrar, onGuardado }) {
  const [importe, setImporte] = useState('');
  const [metodo, setMetodo] = useState('EFECTIVO');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const monto = Number(importe || 0);
  const motivoApagado = monto <= 0
    ? 'Escribe cuánto te está pagando.'
    : monto > Number(cliente.saldo)
      ? `${cliente.nombre} solo te debe ${dinero(cliente.saldo)}. Si te paga de más, lo que sobre le quedará a favor.`
      : '';
  // Pagar de más es válido —queda como anticipo—, así que eso NO apaga el
  // botón: solo se avisa. Apagarlo obligaría a apuntarlo fuera de TEGO,
  // que es donde el dinero se pierde.
  const soloAviso = monto > Number(cliente.saldo);

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      const r = await api.post(`/clientes/${cliente.id}/cobros`, {
        importe: String(monto), metodo,
      });
      onGuardado(r.mensaje);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal titulo={`Abono de ${cliente.nombre}`} icono={Wallet}
           subtitulo={`Ahora te debe ${dinero(cliente.saldo)}.`}
           onCerrar={onCerrar} bloqueado={guardando}
           pie={
             <div className="flex flex-col gap-2">
               {motivoApagado && (
                 <p className="text-sm text-amber-700">{motivoApagado}</p>
               )}
               {error && <p className="text-sm text-red-600">{error}</p>}
               <div className="flex justify-end gap-2">
                 <button type="button" onClick={onCerrar} disabled={guardando}
                         className="px-4 py-2.5 font-semibold text-gray-600">
                   Cancelar
                 </button>
                 <button type="button" onClick={guardar}
                         disabled={monto <= 0 || guardando}
                         className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5
                                    font-semibold text-white bg-[var(--color-marca)]
                                    disabled:bg-gray-300">
                   {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                   Registrar abono
                 </button>
               </div>
             </div>
           }>
      <div className="flex flex-col gap-3">
        <NotaExplicativa titulo="Un abono no se puede editar ni borrar">
          Queda escrito con tu nombre y la hora. Si te equivocas, se corrige
          registrando otro movimiento — igual que los movimientos de mercancía.
        </NotaExplicativa>
        <Campo etiqueta="¿Cuánto te está pagando?" obligatorio tipo="number"
               valor={importe} onChange={(e) => setImporte(e.target.value)} />
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">¿Cómo te pagó?</span>
          <select value={metodo} onChange={(e) => setMetodo(e.target.value)}
                  className="rounded-xl border border-gray-300 px-3 py-2.5">
            <option value="EFECTIVO">Efectivo</option>
            <option value="TARJETA">Tarjeta</option>
            <option value="TRANSFERENCIA">Transferencia</option>
            <option value="CHEQUE">Cheque</option>
          </select>
        </label>
        {soloAviso && (
          <p className="flex items-start gap-2 text-sm text-gray-600">
            <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            No pasa nada: lo que sobre se le queda a favor para su próxima compra.
          </p>
        )}
      </div>
    </Modal>
  );
}

// Un campo con su ayuda de una frase al lado, como manda la doctrina:
// nada de mandar a un manual para saber qué va aquí.
function Campo({ etiqueta, valor, onChange, ayuda, obligatorio, tipo = 'text' }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-semibold text-gray-700">
        {etiqueta}
        {obligatorio && <span className="ml-1 text-red-600" aria-hidden="true">*</span>}
      </span>
      <input type={tipo} value={valor} onChange={onChange}
             className="rounded-xl border border-gray-300 px-3 py-2.5" />
      {ayuda && <span className="text-xs text-gray-500">{ayuda}</span>}
    </label>
  );
}


// ------------------------------------------------- estado de cuenta
// DE DONDE VIENE CADA PESO, en orden y con el saldo acumulado a la
// derecha. Un total suelto —«te debe $4 320»— no se puede discutir con
// el cliente cuando dice que ya pagó; el renglón con su fecha y su
// folio, sí.
function EstadoCuenta({ cliente, onCerrar }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/clientes/${cliente.id}/estado-cuenta`)
      .then(setD)
      .catch((e) => setError(e.message));
  }, [cliente.id]);

  return (
    <Modal titulo={`Estado de cuenta · ${cliente.nombre}`} icono={FileText}
           ancho="max-w-2xl" onCerrar={onCerrar}
           subtitulo={d ? `Te debe ${dinero(d.saldo)}.` : 'Cargando…'}>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!d && !error && (
        <p className="flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Buscando sus movimientos…
        </p>
      )}
      {d && d.movimientos.length === 0 && (
        <EstadoVacio
          icono={FileText}
          titulo="Todavía no le has fiado nada"
          descripcion="Aquí aparecerán sus compras a crédito y sus abonos, en orden, con el saldo que le queda después de cada uno."
        />
      )}
      {d && d.movimientos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-1 pr-3">Fecha</th>
                <th className="py-1 pr-3">Concepto</th>
                <th className="py-1 pr-3 text-right">Le fiaste</th>
                <th className="py-1 pr-3 text-right">Te pagó</th>
                <th className="py-1 text-right">Debe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.movimientos.map((m, i) => (
                <tr key={`${m.tipo}-${m.folio}-${i}`}>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-gray-600">
                    {m.fecha}
                    {m.vencido && (
                      <span className="block text-xs font-semibold text-red-600">
                        venció el {m.vence}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-gray-900">
                    {m.concepto}
                    {m.folio != null && (
                      <span className="text-gray-400"> · folio {m.folio}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    {Number(m.cargo) > 0 ? dinero(m.cargo) : ''}
                  </td>
                  <td className="py-1.5 pr-3 text-right text-green-700">
                    {Number(m.abono) !== 0 ? dinero(m.abono) : ''}
                  </td>
                  <td className="py-1.5 text-right font-semibold text-gray-900">
                    {dinero(m.saldo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ----------------------------------------------------- antigüedad
// CUÁNTO TE DEBEN Y DESDE CUÁNDO. Los tramos se cuentan desde el día en
// que VENCIÓ el documento y no desde que se emitió: un documento a 30
// días emitido hace 40 lleva 10 de retraso, no 40 — y confundirlo hace
// que todo el mundo parezca moroso.
function Antiguedad({ onVolver }) {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/clientes/informes/antiguedad')
      .then(setFilas)
      .catch((e) => setError(e.message));
  }, []);

  const columnas = [
    { etiqueta: 'Cliente', valor: (f) => f.cliente, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Aún no vence', tipo: 'moneda', valor: (f) => f.por_vencer,
      render: (f) => <span className="text-gray-500">{dinero(f.por_vencer)}</span> },
    { etiqueta: '1 a 30 días', tipo: 'moneda', valor: (f) => f.de_1_a_30,
      render: (f) => <span className="text-amber-600">{dinero(f.de_1_a_30)}</span> },
    { etiqueta: '31 a 60', tipo: 'moneda', valor: (f) => f.de_31_a_60,
      render: (f) => <span className="text-amber-700">{dinero(f.de_31_a_60)}</span> },
    { etiqueta: '61 a 90', tipo: 'moneda', valor: (f) => f.de_61_a_90,
      render: (f) => <span className="text-red-600">{dinero(f.de_61_a_90)}</span> },
    { etiqueta: 'Más de 90', tipo: 'moneda', valor: (f) => f.mas_de_90,
      render: (f) => <span className="font-semibold text-red-700">{dinero(f.mas_de_90)}</span> },
    { etiqueta: 'Total', tipo: 'moneda', valor: (f) => f.total,
      render: (f) => <span className="font-semibold">{dinero(f.total)}</span> },
  ];

  return (
    <>
      <div className="px-4 pt-3">
        <button type="button" onClick={onVolver}
                className="text-sm font-semibold text-[var(--color-marca)]">
          ← Volver a la lista de clientes
        </button>
      </div>
      <ListaEstandar
        titulo="Cuánto te deben y desde cuándo"
        subtitulo="Lo que sigue sin cobrarse, agrupado por los días que lleva vencido."
        nota={
          <NotaExplicativa titulo="Los días se cuentan desde que venció, no desde la venta">
            Una venta a 30 días hecha hace 40 lleva <strong>10 días</strong> de
            retraso, no 40. Contarlo desde la venta haría parecer moroso a quien
            todavía está en plazo.
          </NotaExplicativa>
        }
        columnas={columnas}
        filas={filas || []}
        cargando={filas === null && !error}
        claveFila={(f) => f.id_cliente}
        exportar={{
          nombre: 'antiguedad-de-saldos',
          columnas: [
            { etiqueta: 'Cliente', valor: (f) => f.cliente },
            { etiqueta: 'Aún no vence', tipo: 'moneda', valor: (f) => f.por_vencer },
            { etiqueta: '1 a 30 días', tipo: 'moneda', valor: (f) => f.de_1_a_30 },
            { etiqueta: '31 a 60 días', tipo: 'moneda', valor: (f) => f.de_31_a_60 },
            { etiqueta: '61 a 90 días', tipo: 'moneda', valor: (f) => f.de_61_a_90 },
            { etiqueta: 'Más de 90 días', tipo: 'moneda', valor: (f) => f.mas_de_90 },
            { etiqueta: 'Total', tipo: 'moneda', valor: (f) => f.total },
          ],
        }}
        vacio={
          <EstadoVacio
            icono={CalendarClock}
            titulo="Nadie te debe nada"
            descripcion="Aquí aparecerá lo que hayas fiado y siga sin cobrarse, agrupado por los días que lleve vencido, para que sepas a quién llamar primero."
          />
        }
      />
    </>
  );
}
